import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, addDoc, collection, getDocs } from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError, showSuccess } from '../utils/notifications';
import LoadingIndicator from '../components/LoadingIndicator';
import * as XLSX from 'xlsx';
import { getClientInfo } from '../services/auditService';
// Removed ticketService import - now using Firestore directly

function TicketReports() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('ticket-reports');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Estado para el reporte
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });  // Removed additional filters - only keeping date filtering
  const [showFilters, setShowFilters] = useState(false);

  // Estado para exportación
  const [showExportModal, setShowExportModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const ticketsPerPage = 10;

  // Calcular tickets para la página actual
  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirstTicket, indexOfLastTicket);
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);

  // Función para cambiar de página
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Función para ir a la página anterior
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Función para ir a la página siguiente
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Verificación de autenticación y permisos
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }

          const userData = userDoc.data();
          console.log("🔥 TicketReports: userData de Firestore:", userData);
          console.log("🔥 TicketReports: Tipo de rol:", typeof userData.rol);
          console.log("🔥 TicketReports: Rol exacto:", userData.rol);
          const role = userData.rol || 'Usuario';
          
          console.log("🔥 TicketReports: Usuario autenticado:", currentUser?.email);
          console.log("🔥 TicketReports: Rol determinado desde DB:", role);
          console.log("🔥 TicketReports: Rol normalizado:", role.trim());
          
          // Verificar si el usuario tiene uno de los roles permitidos
          const allowedRoles = ['Admin', 'Recaudador', 'Auditor'];
          if (!allowedRoles.includes(role)) {
            console.log("🔥 TicketReports: Rol no permitido:", role);
            showError("Acceso denegado", "Solo usuarios con los siguientes roles pueden acceder a esta sección: Admin, Recaudador o Auditor");
            navigate('/dashboard');
            return;
          }
          
          setUser(currentUser);
          setUserRole(role);
          // Guardar el rol en localStorage para consistencia
          localStorage.setItem('userRole', role);
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Cargar tickets del día actual al iniciar
  useEffect(() => {
    if (user && ['Admin', 'Recaudador', 'Auditor'].includes(userRole)) {
      // Cargar tickets del día actual
      const today = new Date();
      // Usar UTC para evitar problemas de timezone
      const todayString = today.getFullYear() + '-' + 
                         String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                         String(today.getDate()).padStart(2, '0');
      
      console.log('📅 Fecha actual generada:', todayString);
      console.log('📅 Fecha actual completa:', today.toISOString());
      console.log('📅 Fecha local:', today.toLocaleDateString('es-ES'));
      
      setDateFilter({
        startDate: todayString,
        endDate: todayString
      });
      
      // Cargar tickets del día actual (usar setTimeout para evitar problemas de dependencias)
      setTimeout(() => {
        loadTicketsFromFirestore(todayString, todayString);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Cargar solo al montar con user/userRole
  }, [user, userRole]);

  // Agregar función para validar el rango de fechas
  const validateDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 30) {
      showError("Error", "Para mejor rendimiento, el rango de fechas no puede ser mayor a 30 días");
      return false;
    }
    
    if (diffDays > 7) {
      console.warn(`⚠️ Consulta de ${diffDays} días puede tardar más tiempo del habitual`);
    }
    
    return true;
  };

  // Función para manejar la búsqueda con filtros de fecha
  const handleDateFilter = async () => {
    if (!dateFilter.startDate || !dateFilter.endDate) {
      showError("Error", "Por favor selecciona un rango de fechas válido");
      return;
    }

    if (!validateDateRange(dateFilter.startDate, dateFilter.endDate)) {
      return;
    }

    await loadTickets();
  };

  // Modificar el useEffect del filtrado - ahora solo actualiza la vista
  useEffect(() => {
    // Los tickets ya vienen filtrados del servidor, solo los mostramos
    setFilteredTickets(tickets);
    setCurrentPage(1); // Reset a la primera página cuando cambian los tickets
  }, [tickets]);

  // Helper function to parse Spanish date format
  const parseSpanishDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      console.log('Fecha inválida (no es string o está vacía):', dateString);
      return null;
    }
    
    try {
      // Comprobar si es un objeto Firebase Timestamp (formato antiguo)
      if (dateString.seconds !== undefined && dateString.nanoseconds !== undefined) {
        console.log('Parseando fecha tipo Timestamp:', dateString);
        return new Date(dateString.seconds * 1000);
      }
      
      // Manejar formato: "24 de mayo de 2025, 11:18:21 p. m. GMT-05:00"
      if (dateString.includes(' de ')) {
        const parts = dateString.split(',')[0].trim(); // "24 de mayo de 2025"
        const timePart = dateString.split(',')[1]?.trim(); // "11:18:21 p. m. GMT-05:00"
        
        const dateParts = parts.split(' de ');
        
        if (dateParts.length >= 3) {
          const day = parseInt(dateParts[0].trim());
          const monthName = dateParts[1].trim().toLowerCase();
          const year = parseInt(dateParts[2].trim());
          
          const monthMap = {
            'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
            'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
            'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
          };
          
          const month = monthMap[monthName];
          if (month !== undefined) {
            const date = new Date(year, month, day);
            
            // Agregar hora si está disponible
            if (timePart) {
              const timeMatch = timePart.match(/(\d{1,2}):(\d{2}):(\d{2})\s*([ap]\.\s*m\.)/i);
              if (timeMatch) {
                let [, hours, minutes, seconds, meridiem] = timeMatch;
                hours = parseInt(hours);
                if (meridiem.toLowerCase().includes('p') && hours < 12) {
                  hours += 12;
                } else if (meridiem.toLowerCase().includes('a') && hours === 12) {
                  hours = 0;
                }
                date.setHours(hours, parseInt(minutes), parseInt(seconds));
              }
            }
            
            console.log('Fecha parseada (formato español):', {
              original: dateString,
              parsed: date.toLocaleString('es-ES')
            });
            
            return date;
          }
        }
      }
      
      // Manejar formato: "23:18 24/05/2025"
      const dateTimeMatch = dateString.match(/(\d{2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})/);
      if (dateTimeMatch) {
        const [, hours, minutes, day, month, year] = dateTimeMatch;
        const date = new Date(year, parseInt(month) - 1, day, hours, minutes);
        
        console.log('Fecha parseada (formato DD/MM/YYYY):', {
          original: dateString,
          parsed: date.toLocaleString('es-ES')
        });
        
        return date;
      }
      
      // Intentar parsear como fecha ISO
      const isoDate = new Date(dateString);
      if (!isNaN(isoDate.getTime())) {
        console.log('Fecha parseada (formato ISO):', {
          original: dateString,
          parsed: isoDate.toLocaleString('es-ES')
        });
        return isoDate;
      }
      
      console.log('No se pudo parsear la fecha:', dateString);
      
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
    }
    
    return null;
  };

  // Helper function to format Spanish date for display
  const formatSpanishDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // Si ya es un objeto Date, usarlo directamente
      if (dateString instanceof Date) {
        return dateString.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Intentar convertir la cadena a fecha
      const date = parseSpanishDate(dateString);
      if (date) {
        return date.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return dateString; // Return original if parsing fails
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return dateString;
    }
  };

  // Función para generar array de fechas entre startDate y endDate
  const generateDateRange = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Asegurar que las fechas estén en el formato correcto
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    
    const currentDate = new Date(start);
    
    while (currentDate <= end) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Función para cargar tickets desde Firestore
  const loadTicketsFromFirestore = async (startDate, endDate) => {
    setIsLoadingData(true);
    try {
      console.log('🔍 Cargando tickets desde Firestore:', { startDate, endDate });
      
      // Generar rango de fechas
      const dateRange = generateDateRange(startDate, endDate);
      console.log(`📅 Rango de fechas generado: ${dateRange.length} días`);
      console.log(`📅 Fechas a buscar:`, dateRange);
      
      const allTickets = [];
      
      // Iterar sobre cada fecha en el rango
      for (const dateStr of dateRange) {
        try {
          console.log(`🔎 Buscando tickets para fecha: ${dateStr}`);
          
          // Intentar acceder directamente a la subcolección
          // En Firestore, las subcolecciones pueden existir incluso si el documento padre no tiene campos
          const ticketsRef = collection(db, 'ticketsVendidos', dateStr, 'tickets');
          console.log(`📂 Obteniendo subcolección tickets para ${dateStr}...`);
          
          // Intentar obtener la subcolección directamente
          const ticketsSnapshot = await getDocs(ticketsRef);
          
          console.log(`✅ ${dateStr}: ${ticketsSnapshot.size} tickets encontrados en subcolección`);
          
          // Si hay tickets, procesarlos
          if (ticketsSnapshot.size > 0) {
            
            ticketsSnapshot.forEach((ticketDoc) => {
              const ticketData = ticketDoc.data();
              
              // Procesar coordenadas
              let coordinates = null;
              if (ticketData.coordinates) {
                if (typeof ticketData.coordinates === 'string') {
                  const [lat, lng] = ticketData.coordinates.split(',').map(Number);
                  coordinates = { latitude: lat, longitude: lng };
                } else if (ticketData.latitude && ticketData.longitude) {
                  coordinates = {
                    latitude: ticketData.latitude,
                    longitude: ticketData.longitude
                  };
                }
              }
              
              // Calcular duración
              let duration = ticketData.duration || 0;
              if (!duration && ticketData.startTime && ticketData.endTime) {
                const startTime = ticketData.startTime.split(' ')[0];
                const endTime = ticketData.endTime.split(' ')[0];
                const [startHour, startMin] = startTime.split(':').map(Number);
                const [endHour, endMin] = endTime.split(':').map(Number);
                const start = new Date(2000, 0, 1, startHour, startMin);
                const end = new Date(2000, 0, 1, endHour, endMin);
                duration = (end - start) / (1000 * 60 * 60);
              }
              
              // Procesar fecha de creación
              let createdAt = null;
              if (ticketData.createdAt) {
                if (ticketData.createdAt.seconds) {
                  createdAt = new Date(ticketData.createdAt.seconds * 1000);
                } else if (ticketData.createdAt instanceof Date) {
                  createdAt = ticketData.createdAt;
                } else {
                  createdAt = new Date(ticketData.createdAt);
                }
              }
              
              // Asegurar que el precio sea un número
              const basePrice = typeof ticketData.price === 'number' ? ticketData.price : parseFloat(ticketData.price) || 0;
              
              allTickets.push({
                id: ticketDoc.id,
                ...ticketData,
                coordinates,
                duration: duration || 0,
                price: basePrice,
                createdAt: createdAt,
                formattedCreatedAt: formatSpanishDate(createdAt),
                formattedStartTime: ticketData.startTime || 'N/A',
                formattedEndTime: ticketData.endTime || 'N/A',
                ticketPeriod: ticketData.ticketPeriod || 'N/A',
                isActive: ticketData.status === 'active',
                vehicleTypeDisplay: getVehicleTypeDisplay(ticketData.vehicleType),
                location: formatLocation(coordinates),
                dateStr: dateStr // Guardar la fecha del documento para referencia
              });
            });
            
            console.log(`✅ ${dateStr}: ${ticketsSnapshot.size} tickets procesados`);
          } else {
            console.log(`⚠️ ${dateStr}: No se encontraron tickets en la subcolección`);
            console.log(`💡 Verifica que exista la subcolección en Firestore: ticketsVendidos/${dateStr}/tickets/`);
          }
        } catch (error) {
          console.error(`❌ Error cargando tickets para ${dateStr}:`, error);
          console.error(`   Error details:`, error.message);
          // Continuar con la siguiente fecha aunque haya error
        }
      }
      
      console.log(`✅ Total tickets cargados: ${allTickets.length}`);
      
      if (allTickets.length === 0) {
        console.warn(`⚠️ No se encontraron tickets para el rango de fechas: ${startDate} a ${endDate}`);
        console.warn(`💡 Verifica que existan documentos en Firestore para estas fechas`);
        console.warn(`💡 Formato esperado: ticketsVendidos/YYYY-MM-DD/tickets/`);
      }
      
      setTickets(allTickets);
      
    } catch (error) {
      console.error("Error al cargar tickets desde Firestore:", error);
      showError("Error", `No se pudieron cargar los tickets: ${error.message}`);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Función para cargar tickets (mantener compatibilidad)
  const loadTickets = async () => {
    if (dateFilter.startDate && dateFilter.endDate) {
      await loadTicketsFromFirestore(dateFilter.startDate, dateFilter.endDate);
    } else {
      showError("Error", "Por favor selecciona un rango de fechas");
    }
  };

  // Función para normalizar tipo de vehículo
  const normalizeVehicleType = (type) => {
    if (!type) return 'unknown';
    
    const normalizedType = type.toLowerCase().trim();
    
    switch (normalizedType) {
      case 'automovil':
      case 'auto':
      case 'carro':
      case 'car':
        return 'auto';
      case 'motocicleta':
      case 'moto':
      case 'motorcycle':
        return 'moto';
      case 'exonerado':
      case 'exempt':
      case 'free':
        return 'exonerado';
      default:
        console.log(`🔥 TicketReports: Unknown vehicle type: ${type}, treating as unknown`);
        return 'unknown';
    }
  };
  // Función para mostrar tipo de vehículo
  const getVehicleTypeDisplay = (type) => {
    if (!type) return 'N/A';
    
    const normalized = normalizeVehicleType(type);
    switch (normalized) {
      case 'auto': return 'Auto';
      case 'moto': return 'Moto';
      case 'exonerado': return 'Exonerado';
      default: return type; // Return original if unknown
    }
  };

  // Función para exportar a Excel
  const exportToExcel = async () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredTickets.map(ticket => ({
        'ID': ticket.id,
        'Placa': ticket.plate || 'N/A',
        'Tipo de Vehículo': ticket.vehicleTypeDisplay,
        'CA': ticket.numberCa || 'N/A',
        'Nombre': ticket.nombre || 'N/A',
        'Duración': ticket.duration || 0,
        'Precio': ticket.price || 0,
        'Zona': ticket.zone || ticket.zona || 'N/A',
        'Método de Pago': ticket.paymentMethod === 'cash' || ticket.paymentMethod === 'efectivo' ? 'Efectivo' : 
                         ticket.paymentMethod === 'transferencia' ? 'Transferencia' : ticket.paymentMethod || 'N/A',
        'Período': ticket.ticketPeriod || 'N/A',
        'Fecha Creación': ticket.formattedCreatedAt || 'N/A',
        'Hora Inicio': ticket.formattedStartTime || 'N/A',
        'Hora Fin': ticket.formattedEndTime || 'N/A',
        'Estado': ticket.status === 'active' ? 'Activo' : 
                  ticket.status === 'expired' ? 'Expirado' : ticket.status || 'N/A',
        'Ubicación': ticket.location || 'N/A',
        'Coordenadas': ticket.coordinates ? `${ticket.coordinates.latitude}, ${ticket.coordinates.longitude}` : 'N/A'
      })));

      // Calcular estadísticas
      const stats = {
        totalTickets: filteredTickets.reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0),
        activeTickets: filteredTickets.filter(t => t.isActive).length,
        expiredTickets: filteredTickets.filter(t => !t.isActive).length,
        totalRevenue: filteredTickets.reduce((sum, t) => sum + (Number(t.price) || 0), 0),
        motoTickets: filteredTickets
          .filter(t => normalizeVehicleType(t.vehicleType) === 'moto')
          .reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0),
        autoTickets: filteredTickets
          .filter(t => normalizeVehicleType(t.vehicleType) === 'auto')
          .reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0),
        exoneratedTickets: filteredTickets.filter(t => normalizeVehicleType(t.vehicleType) === 'exonerado').length,
        averageDuration: filteredTickets.reduce((sum, t) => {
          const duration = parseFloat(t.duration);
          return sum + (isNaN(duration) ? 0 : duration);
        }, 0) / filteredTickets.length
      };

      // Crear resumen
      const summaryData = [
        ['📊 REPORTE DE TICKETS - RESUMEN'],
        [''],
        ['Período:', dateFilter.startDate && dateFilter.endDate ? 
          `${dateFilter.startDate} a ${dateFilter.endDate}` : 'Todos los registros'],
        ['Generado:', new Date().toLocaleString('es-ES')],
        ['Usuario:', user?.email || 'N/A'],
        [''],
        ['ESTADÍSTICAS GENERALES'],
        ['Total de Tickets', stats.totalTickets],
        ['Tickets Activos', stats.activeTickets],
        ['Tickets Expirados', stats.expiredTickets],
        ['Ingresos Totales', `$${stats.totalRevenue.toFixed(2)}`],
        ['Duración Promedio', `${stats.averageDuration.toFixed(1)} horas`],
        [''],
        ['DISTRIBUCIÓN POR TIPO'],
        ['Tickets Motos', stats.motoTickets],
        ['Tickets Autos', stats.autoTickets],
        ['Tickets Exonerados', stats.exoneratedTickets]
      ];

      // Crear libro de trabajo
      const wb = XLSX.utils.book_new();
      
      // Hoja de resumen
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Aplicar estilos al resumen
      const summaryRange = XLSX.utils.decode_range(summaryWs['!ref']);
      for (let R = 0; R <= summaryRange.e.r; R++) {
        for (let C = 0; C <= summaryRange.e.c; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!summaryWs[cellAddress]) summaryWs[cellAddress] = { v: "" };
          
          // Estilo para el título
          if (R === 0) {
            summaryWs[cellAddress].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "2563EB" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
          // Estilo para los encabezados de sección
          else if (R === 6 || R === 12) {
            summaryWs[cellAddress].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "7C3AED" } },
              alignment: { horizontal: "left", vertical: "center" }
            };
          }
          // Estilo para los valores
          else if ((R > 6 && R < 12) || R > 12) {
            summaryWs[cellAddress].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "F3E8FF" } },
              alignment: { horizontal: "left", vertical: "center" }
            };
          }
        }
      }
      
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Resumen');
      
      // Hoja de datos detallados
      XLSX.utils.book_append_sheet(wb, worksheet, 'Tickets Detallados');

      // Generar nombre del archivo
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const dateInfo = dateFilter.startDate && dateFilter.endDate ? 
        `_${dateFilter.startDate}_a_${dateFilter.endDate}` : '_todos';
      const fileName = `reporte_tickets_admin${dateInfo}_${timestamp}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(wb, fileName);
      
      // Registrar acción en logs de auditoría
      try {
        const currentUserEmail = auth.currentUser ? auth.currentUser.email : 'Desconocido';
        const userRoleFromState = userRole || 'Desconocido';
        const clientInfo = await getClientInfo();

        await addDoc(collection(db, 'auditLogs'), {
          timestamp: new Date(),
          userId: currentUserEmail,
          userRole: userRoleFromState,
          accion: 'Descarga de Reporte',
          categoria: 'reportes',
          entidad: currentUserEmail,
          detalle: {
            nombreArchivo: fileName,
            cantidadRegistros: filteredTickets.length,
            fechaInicioFiltro: dateFilter.startDate || 'N/A',
            fechaFinFiltro: dateFilter.endDate || 'N/A',
          },
          mensaje: `Usuario ${currentUserEmail} descargó el reporte de tickets: ${fileName}`,
          ip: clientInfo.ip,
          dispositivo: clientInfo.userAgent || 'N/A',
          success: true,
          error: null,
          reason: null,
          loginMethod: null,
          nonce: null,
          retryAttempt: 0,
          serverTimestamp: new Date(),
          usuarioRef: auth.currentUser ? `usuarios/${auth.currentUser.email}` : null,
         });
        console.log("Acción de descarga de reporte de tickets registrada en audit-logs");
      } catch (logError) {
        console.error("Error registrando acción en audit-logs:", logError);
      }
      
      showSuccess("Exportación exitosa", `Archivo descargado: ${fileName}`);
      setShowExportModal(false);
    } catch (error) {
      console.error("Error al exportar:", error);
      showError("Error", "No se pudo exportar el archivo");
    }
  };  // Función para limpiar filtros de fecha
  const clearFilters = () => {
    setDateFilter({ startDate: '', endDate: '' });
    setTickets([]); // Limpiar tickets al limpiar filtros
    setFilteredTickets([]); // También limpiar filteredTickets
  };

  // Modificar los botones de filtro rápido
  const handleQuickFilter = (days) => {
    if (days > 90) {
      showError("Error", "El rango de fechas no puede ser mayor a 90 días");
      return;
    }

    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - days);
    
    setDateFilter({
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    });
    
    // Cargar tickets después de establecer el filtro
    loadTickets();
  };

  const formatLocation = (coordinates) => {
    if (!coordinates) return 'N/A';
    return `${(coordinates.latitude && coordinates.latitude.toFixed(6)) || 'N/A'}, ${(coordinates.longitude && coordinates.longitude.toFixed(6)) || 'N/A'}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <LoadingIndicator size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Reportes de Tickets" 
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Top spacing for mobile */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
        
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Desktop Header */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">Reportes de Tickets para Administradores</h1>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole}</p>
                </div>
                <button 
                  onClick={async () => {
                    try {
                      localStorage.removeItem('userRole');
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error al cerrar sesión:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="p-6">
            {/* Controls Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 lg:mb-0">Filtros y Exportación</h2>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                    </svg>
                    {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                  </button>
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Exportar
                  </button>
                </div>
              </div>              {/* Filters Panel */}
              {showFilters && (
                <div className="border-t pt-4">
                  {/* Date Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Inicio</label>
                      <input
                        type="date"
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin</label>
                      <input
                        type="date"
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Filter Buttons */}
                  <div className="flex justify-end space-x-3 mb-4">
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-medium flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Limpiar Filtros
                    </button>
                    <button
                      onClick={handleDateFilter}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                      </svg>
                      Aplicar Filtro
                    </button>
                  </div>

                  {/* Date Range Presets */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      onClick={() => handleQuickFilter(1)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                    >
                      Últimas 24 horas
                    </button>
                    <button
                      onClick={() => handleQuickFilter(7)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                    >
                      Última semana
                    </button>
                    <button
                      onClick={() => handleQuickFilter(30)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                    >
                      Último mes
                    </button>
                    <button
                      onClick={() => handleQuickFilter(90)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                    >
                      Últimos 3 meses
                    </button>
                  </div>
                </div>
              )}
            </div>            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Total Tickets</p>
                    <p className="text-2xl font-bold text-blue-600">{filteredTickets.reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0)}</p>
                  </div>
                  <div className="text-4xl text-blue-500">🎫</div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Tickets Motos</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {filteredTickets
                        .filter(t => normalizeVehicleType(t.vehicleType) === 'moto')
                        .reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0)}
                    </p>
                  </div>
                  <div className="text-4xl text-blue-500">🏍️</div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Tickets Autos</p>
                    <p className="text-2xl font-bold text-green-600">
                      {filteredTickets
                        .filter(t => normalizeVehicleType(t.vehicleType) === 'auto')
                        .reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0)}
                    </p>
                  </div>
                  <div className="text-4xl text-green-500">🚗</div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Ingresos Totales</p>
                    <p className="text-2xl font-bold text-purple-600">
                      ${filteredTickets.reduce((sum, t) => sum + (Number(t.price) || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-4xl text-purple-500">💰</div>
                </div>
              </div>
            </div>

            {/* Tickets Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Lista de Tickets ({filteredTickets.length})
                  </h2>
                  {isLoadingData && <LoadingIndicator size="small" />}
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Placa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo Vehículo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CA
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duración
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Precio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zona
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Método Pago
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Período
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Creado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentTickets.map((ticket) => (
                      <tr key={ticket.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono font-medium text-gray-900">{ticket.id || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{ticket.plate || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (normalizeVehicleType(ticket.vehicleType) === 'moto') 
                              ? 'bg-blue-100 text-blue-800' 
                              : (normalizeVehicleType(ticket.vehicleType) === 'auto')
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {normalizeVehicleType(ticket.vehicleType) === 'moto' ? '🏍️ Moto' : 
                             normalizeVehicleType(ticket.vehicleType) === 'auto' ? '🚗 Auto' :
                             normalizeVehicleType(ticket.vehicleType) === 'exonerado' ? '🛡️ Exonerado' : 
                             getVehicleTypeDisplay(ticket.vehicleType)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {ticket.numberCa || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {ticket.nombre || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ticket.duration ? `${ticket.duration}h` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ${ticket.price ? ticket.price.toFixed(2) : '0.00'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {ticket.zone || ticket.zona || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            ticket.paymentMethod === 'cash' || ticket.paymentMethod === 'efectivo'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-indigo-100 text-indigo-800'
                          }`}>
                            {ticket.paymentMethod === 'cash' || ticket.paymentMethod === 'efectivo' ? '💵 Efectivo' : 
                             ticket.paymentMethod === 'transferencia' ? '🏦 Transferencia' : 
                             ticket.paymentMethod || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ticket.ticketPeriod || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ticket.formattedCreatedAt || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            ticket.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {ticket.status === 'active' ? '✅ Activo' : 
                             ticket.status === 'expired' ? '⏰ Expirado' :
                             ticket.status || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {currentTickets.length === 0 && !isLoadingData && (
                      <tr>
                        <td colSpan="12" className="px-6 py-4 text-center">
                          <div className="space-y-2">
                            <p className="text-gray-500 font-medium">
                              No se encontraron tickets con los filtros aplicados.
                            </p>
                            {dateFilter.startDate && dateFilter.endDate && (
                              <div className="text-xs text-gray-400 space-y-1">
                                <p>Buscando en el rango: <span className="font-semibold">{dateFilter.startDate}</span> a <span className="font-semibold">{dateFilter.endDate}</span></p>
                                <p className="text-blue-600">
                                  💡 Verifica que existan documentos en Firestore: <code className="bg-gray-100 px-1 rounded">ticketsVendidos/YYYY-MM-DD/tickets/</code>
                                </p>
                                <p className="text-gray-500">
                                  Si necesitas buscar en otra fecha, usa los filtros de fecha arriba.
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {filteredTickets.length > 0 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="sm:hidden">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === 1 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-700">
                        Página {currentPage} de {totalPages}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === totalPages 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{indexOfFirstTicket + 1}</span> a{' '}
                        <span className="font-medium">
                          {Math.min(indexOfLastTicket, filteredTickets.length)}
                        </span>{' '}
                        de <span className="font-medium">{filteredTickets.length}</span> tickets
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px min-w-max" aria-label="Pagination">
                        <button
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === 1 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Anterior</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {(() => {
                          const maxVisiblePages = 5;
                          const halfVisible = Math.floor(maxVisiblePages / 2);
                          let startPage = Math.max(1, currentPage - halfVisible);
                          let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                          
                          // Ajustar si estamos cerca del final
                          if (endPage - startPage < maxVisiblePages - 1) {
                            startPage = Math.max(1, endPage - maxVisiblePages + 1);
                          }
                          
                          const pages = [];
                          
                          // Botón "Primera página" si no estamos en el inicio
                          if (startPage > 1) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => handlePageChange(1)}
                                className="relative inline-flex items-center px-4 py-2 border bg-white border-gray-300 text-gray-500 hover:bg-gray-50 text-sm font-medium"
                              >
                                1
                              </button>
                            );
                            if (startPage > 2) {
                              pages.push(
                                <span key="dots1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                  ...
                                </span>
                              );
                            }
                          }
                          
                          // Páginas visibles
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === i
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }
                          
                          // Botón "Última página" si no estamos en el final
                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span key="dots2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => handlePageChange(totalPages)}
                                className="relative inline-flex items-center px-4 py-2 border bg-white border-gray-300 text-gray-500 hover:bg-gray-50 text-sm font-medium"
                              >
                                {totalPages}
                              </button>
                            );
                          }
                          
                          return pages;
                        })()}
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === totalPages 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Siguiente</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Exportar Reporte</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Se exportarán <strong>{filteredTickets.length}</strong> tickets con los filtros aplicados.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={exportToExcel}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              >
                Exportar a Excel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketReports;
